import React, { useState, useEffect } from 'react';
import { Modal, Button, Space, Checkbox, List, Input, Pagination, message, Table } from 'antd';
import './ViewSolicitacaoModalADM.css';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { handleApiResponse, handleApiError } from '../../../utils/apiMessages';
import API_CONFIG from '../../../config/api.config';
import veiculoService from '../../../services/veiculoService';

/** Adicione o hook useIsMobile no topo do arquivo **/
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

const ViewSolicitacaoModal = ({ isVisible, onClose, solicitacao }) => {
  const navigate = useNavigate();
  const [fornecedoresModalVisible, setFornecedoresModalVisible] = useState(false);
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedoresSelecionados, setFornecedoresSelecionados] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [veiculoInfo, setVeiculoInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fornecedoresPaginacao, setFornecedoresPaginacao] = useState(null);
  const pageSize = 8;
  const [showMainModal, setShowMainModal] = useState(true);

  const isMobile = useIsMobile();

  // Função para limpar todos os estados
  const limparEstados = () => {
    setFornecedoresModalVisible(false);
    setFornecedores([]);
    setFornecedoresSelecionados([]);
    setSearchText('');
    setCurrentPage(1);
  };

  // Efeito para limpar estados quando o modal principal for fechado
  useEffect(() => {
    if (!isVisible) {
      limparEstados();
    }
  }, [isVisible]);

  const handleCloseFornecedoresModal = () => {
    setFornecedoresModalVisible(false);
    if (isMobile) {
      setShowMainModal(true);
    }
    limparEstados();
  };

  const handleEnviarSolicitacao = () => {
    if (isMobile) {
      setShowMainModal(false);
      setFornecedoresModalVisible(true);
    } else {
      setFornecedoresModalVisible(true);
    }
  };

  const handleConfirmarEnvio = async () => {
    if (fornecedoresSelecionados.length === 0) {
      Modal.error({
        title: 'Erro',
        content: 'Selecione pelo menos um fornecedor para enviar a solicitação.'
      });
      return;
    }

    try {
      // Envio real para a API
      const payload = {
        SOL_ID: solicitacao.SOL_ID,
        FOR_ID: fornecedoresSelecionados
      };
      const response = await api.post(API_CONFIG.ENDPOINTS.SOLICITACOES.ENVIAR, payload);
      if (response.data.status) {
        message.success('Solicitação enviada com sucesso!');
        handleCloseFornecedoresModal();
        onClose();
      } else {
        message.error(response.data.message || 'Erro ao enviar solicitação para fornecedores.');
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  // Debug: mostrar dados da solicitação
  useEffect(() => {
    if (isVisible && solicitacao) {
     //console.log('DEBUG solicitacao:', solicitacao);
    }
  }, [isVisible, solicitacao]);

  // Carregar fornecedores quando o modal abrir
  useEffect(() => {
    if (fornecedoresModalVisible) {
      carregarFornecedores(1);
    }
    // eslint-disable-next-line
  }, [fornecedoresModalVisible]);

  // Função para buscar fornecedores da API
  const carregarFornecedores = async (pagina = 1) => {
    try {
      setLoading(true);
      const params = { page: pagina, per_page: pageSize };
      const response = await api.get(API_CONFIG.ENDPOINTS.FORNECEDOR.CONSULTAR_FOR, { params });
      if (response.data.status && response.data.fornecedores) {
        setFornecedoresPaginacao(response.data.fornecedores);
        setFornecedores(response.data.fornecedores.data.map(f => ({
          id: f.FOR_ID,
          nome: f.FOR_RAZAO_SOCIAL,
          cnpj: f.FOR_CNPJ,
          endereco: `${f.endereco?.END_LOGRADOURO || ''}, ${f.endereco?.END_CIDADE || ''}`
        })));
      } else {
        setFornecedores([]);
        setFornecedoresPaginacao(null);
      }
    } catch (error) {
      setFornecedores([]);
      setFornecedoresPaginacao(null);
    } finally {
      setLoading(false);
    }
  };

  // Paginação dos fornecedores
  const handleChangePage = (page) => {
    setCurrentPage(page);
    carregarFornecedores(page);
  };

  const handleEdit = () => {
    const token = localStorage.getItem('token');

    if (!token) {
      message.error('Sessão expirada. Por favor, faça login novamente.');
      navigate('/login');
      return;
    }

    onClose();
    navigate('/adm/editar-solicitacao', {
      state: {
        solicitacao,
        token
      }
    });
  };

  const handleCancel = () => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Você está cancelando uma solicitação, tem certeza que deseja continuar?',
      okText: 'Sim',
      cancelText: 'Não',
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');

          if (!token) {
            message.error('Sessão expirada. Por favor, faça login novamente.');
            navigate('/login');
            return;
          }

          const payload = {
            SOL_PRE_ID: Number(solicitacao.SOL_PRE_ID),
            SOL_USU_ID: Number(solicitacao.SOL_USU_ID),
            SOL_VEI_ID: Number(solicitacao.SOL_VEI_ID),
            SOL_NOME_ITEM: solicitacao.SOL_NOME_ITEM,
            SOL_DESCRICAO: solicitacao.SOL_DESCRICAO,
            SOL_STATUS: 'CANCELADA'
          };

          const response = await api.put(
            API_CONFIG.ENDPOINTS.SOLICITACOES.ATUALIZAR(solicitacao.SOL_ID),
            payload
          );

          if (response.data.status) {
            message.success('Solicitação cancelada com sucesso!');
            onClose();
          } else {
            message.error(response.data.message || 'Erro ao cancelar solicitação');
          }
        } catch (error) {
          //console.error('Erro ao cancelar solicitação:', error);
          if (error.response?.status === 401) {
            message.error('Sessão expirada. Por favor, faça login novamente.');
            navigate('/login');
          } else {
            message.error('Erro ao cancelar solicitação');
          }
        }
      },
      okButtonProps: {
        danger: true
      }
    });
  };

  // Função para buscar informações do veículo
  const buscarVeiculo = async (veiculoId) => {
    try {
      setLoading(true);
      const response = await veiculoService.buscarVeiculo(veiculoId);
      if (response.status) {
        setVeiculoInfo(response.veiculo);
      } else {
        message.error('Erro ao carregar informações do veículo');
      }
    } catch (error) {
      //console.error('Erro ao buscar veículo:', error);
      message.error('Erro ao carregar informações do veículo');
    } finally {
      setLoading(false);
    }
  };

  // Efeito para buscar informações do veículo quando o modal abrir
  useEffect(() => {
    if (isVisible && solicitacao?.SOL_VEI_ID) {
      buscarVeiculo(solicitacao.SOL_VEI_ID);
    }
  }, [isVisible, solicitacao]);

  const filteredFornecedores = fornecedores
    .filter(fornecedor =>
      fornecedor.nome.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => {
      // Se a estiver selecionado e b não, a vem primeiro
      if (fornecedoresSelecionados.includes(a.id) && !fornecedoresSelecionados.includes(b.id)) {
        return -1;
      }
      // Se b estiver selecionado e a não, b vem primeiro
      if (!fornecedoresSelecionados.includes(a.id) && fornecedoresSelecionados.includes(b.id)) {
        return 1;
      }
      // Se ambos estiverem selecionados ou não selecionados, mantém a ordem original
      return 0;
    });

  // Calcular itens da página atual
  const currentFornecedores = fornecedores;

  const renderFooter = () => {
    return (
      <div className="modal-footer-buttons">
        <Space size={isMobile ? 8 : 12} direction={isMobile ? "vertical" : "horizontal"} style={{ width: '100%' }}>
          <Button
            type="primary"
            className="standard-button"
            onClick={handleEnviarSolicitacao}
          >
            Enviar
          </Button>

          <Button
            type="primary"
            className="standard-button"
            onClick={() => navigate(
              `/adm/cotacao/${solicitacao?.SOL_ID}`,
              { state: { idPrefeitura: solicitacao?.SOL_PRE_ID, idSolicitacao: solicitacao?.SOL_ID, nomePrefeitura: solicitacao?.NOME_PREFEITURA } }
            )}
          >
            Ver cotação
          </Button>

          {(solicitacao?.SOL_STATUS === "ABERTA" || solicitacao?.SOL_STATUS === "EM_COTACAO") && (
            <>
              {solicitacao?.SOL_STATUS === "ABERTA" && (
                <Button
                  danger
                  id="btn-cancelar-forcado"
                  onClick={handleCancel}
                  style={{
                    background: '#fff',
                    color: '#d32f2f',
                    border: '1.5px solid #d32f2f',
                    height: 48,
                    fontSize: '1.15rem',
                    minWidth: 160
                  }}
                >
                  Cancelar
                </Button>
              )}

              <Button
                type="primary"
                className="standard-button"
                onClick={handleEdit}
              >
                Ver itens
              </Button>
            </>
          )}
        </Space>
      </div>
    );
  };
  // Bloqueia/desbloqueia o scroll do body, html e .home-container ao abrir/fechar o modal de fornecedores
  useEffect(() => {
    const homeContainer = document.querySelector('.home-container');
    if (fornecedoresModalVisible) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      if (homeContainer && isMobile) {
        homeContainer.classList.add('no-scroll');
      }
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (homeContainer && isMobile) {
        homeContainer.classList.remove('no-scroll');
      }
    }
    // Limpa ao desmontar
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (homeContainer && isMobile) {
        homeContainer.classList.remove('no-scroll');
      }
    };
  }, [fornecedoresModalVisible, isMobile]);

  return (
    <>
      {showMainModal && (
        <Modal
          title={`Solicitação #${solicitacao?.SOL_ID}`}
          open={isVisible}
          onCancel={() => {
            limparEstados();
            onClose();
          }}
          width={isMobile ? undefined : 800}
          centered
          footer={renderFooter()}
          destroyOnClose={true}
          maskClosable={true}
          afterClose={limparEstados}
          getContainer={false}
          style={{ position: 'relative', zIndex: 1000 }}
          wrapClassName={isMobile ? 'modal-mobile-fullscreen' : ''}
        >
          <div className="solicitacao-info">
            {loading ? (
              <div className="loading">Carregando informações do veículo...</div>
            ) : (
              <>
                <p><strong>Veículo:</strong> {veiculoInfo?.VEI_DESCRICAO || 'Carregando...'} ({veiculoInfo?.VEI_PLACA || 'Carregando...'})</p>
                <p><strong>Status:</strong> {solicitacao?.SOL_STATUS}</p>
                <p><strong>Data de Abertura:</strong> {new Date(solicitacao?.SOL_DATA_VINCULACAO).toLocaleDateString('pt-BR')}</p>
                <p><strong>Tipo de Veículo:</strong> {veiculoInfo?.VEI_TIPO || 'Carregando...'}</p>
              </>
            )}
          </div>
        </Modal>
      )}

      {fornecedoresModalVisible && (
        <Modal
          title="Selecionar Fornecedores"
          open={fornecedoresModalVisible}
          onCancel={handleCloseFornecedoresModal}
          width={600}
          centered
          wrapClassName="modal--vSoli-responsivo"
          getContainer={false}
          footer={[
            <Button key="cancel" onClick={handleCloseFornecedoresModal}
              style={{
                background: '#fff',
                color: '#d32f2f',
                border: '1.5px solid #d32f2f',
                height: 40,
                fontSize: '1.15rem',
                minWidth: 120
              }}
            >
              Cancelar
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={handleConfirmarEnvio}
              style={{
                background: 'linear-gradient(90deg, #001f4d 60%, #003380 100%)',
                color: '#fff',
                border: 'none',
                height: 40,
                fontSize: '1.15rem',
                minWidth: 120
              }}
            >
              Confirmar Envio
            </Button>
          ]}
          style={{ zIndex: 1001 }}
        >
          <Input
            placeholder="Buscar fornecedores..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
            style={{ marginBottom: 16 }}
          />
          {fornecedoresSelecionados.length > 0 && (
            <div className="selecionados-counter" style={{
              marginBottom: 16,
              padding: '8px 12px',
              backgroundColor: '#f0f7ff',
              borderRadius: 6,
              border: '1px solid #d0e3ff',
              color: '#001f4d',
              fontSize: '0.9rem'
            }}>
              {fornecedoresSelecionados.length} fornecedor{fornecedoresSelecionados.length !== 1 ? 'es' : ''} selecionado{fornecedoresSelecionados.length !== 1 ? 's' : ''}
            </div>
          )}
          <List
            dataSource={currentFornecedores}
            renderItem={(fornecedor) => (
              <List.Item>
                <div style={{ width: '100%' }}>
                  <Checkbox
                    checked={fornecedoresSelecionados.includes(fornecedor.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFornecedoresSelecionados([...fornecedoresSelecionados, fornecedor.id]);
                      } else {
                        setFornecedoresSelecionados(fornecedoresSelecionados.filter(id => id !== fornecedor.id));
                      }
                    }}
                  >
                    <div style={{ marginLeft: 8 }}>
                      <div className="fornecedor-nome" style={{
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        color: fornecedoresSelecionados.includes(fornecedor.id) ? '#001f4d' : 'inherit'
                      }}>
                        {fornecedor.nome}
                        {fornecedoresSelecionados.includes(fornecedor.id) && (
                          <span className="selecionado-badge" style={{
                            marginLeft: 8,
                            fontSize: '0.8rem',
                            backgroundColor: '#001f4d',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: 12
                          }}>
                            Selecionado
                          </span>
                        )}
                      </div>
                      <div className="fornecedor-info" style={{ color: '#666', fontSize: '0.9rem' }}>CNPJ: {fornecedor.cnpj}</div>
                      <div className="fornecedor-info" style={{ color: '#666', fontSize: '0.9rem' }}>{fornecedor.endereco}</div>
                    </div>
                  </Checkbox>
                </div>
              </List.Item>
            )}
          />
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Pagination
              current={fornecedoresPaginacao?.current_page || 1}
              total={fornecedoresPaginacao?.total || 0}
              pageSize={fornecedoresPaginacao?.per_page || pageSize}
              onChange={handleChangePage}
              showSizeChanger={false}
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default ViewSolicitacaoModal; 